import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import './GameSummary.css'

import Footer from '../UIElements/Footer'

const GameSummary = () => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState()

	const [loadedGame, setLoadedGame] = useState()
	const [loadedGameStats, setLoadedGameStats] = useState()
	const [winner, setWinner] = useState()
	const [loser, setLoser] = useState()

	const [homeRoster, setHomeRoster] = useState([])
	const [visitorRoster, setVisitorRoster] = useState([])

	const [allGoalsFromThisGame, setAllGoalsFromThisGame] = useState([])
	const [allPenaltiesFromThisGame, setAllPenaltiesFromThisGame] = useState([])

	const [homeGoalsTotal, setHomeGoalsTotal] = useState(0)
	const [visitorGoalsTotal, setVisitorGoalsTotal] = useState(0)

	const [leagueName, setLeagueName] = useState()

	const { gameId } = useParams()

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/game/${gameId}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Could not load the game summary.',
					)
				}

				setLoadedGame(responseData.game)
				setLoadedGameStats(responseData.gameStats)
				setWinner(responseData.winner)
				setLoser(responseData.loser)

				setAllGoalsFromThisGame(
					Array.isArray(responseData.allGoalsFromThisGame)
						? responseData.allGoalsFromThisGame
						: [],
				)

				setAllPenaltiesFromThisGame(
					Array.isArray(responseData.allPenaltiesFromThisGame)
						? responseData.allPenaltiesFromThisGame
						: [],
				)

				setHomeGoalsTotal(Number(responseData.homeGoalsTotal || 0))
				setVisitorGoalsTotal(Number(responseData.visitorGoalsTotal || 0))
			} catch (err) {
				setError(err.message || 'Could not load the game summary.')
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [gameId])

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/${gameId}/rostersAndPoints`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Could not load the scoring summary.',
					)
				}

				setHomeRoster(
					Array.isArray(responseData.homeRoster) ? responseData.homeRoster : [],
				)

				setVisitorRoster(
					Array.isArray(responseData.visitorRoster)
						? responseData.visitorRoster
						: [],
				)

				setLeagueName(responseData.leagueName)
			} catch (err) {
				setError(err.message || 'Could not load the scoring summary.')
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [gameId])

	const allPlayers = [...homeRoster, ...visitorRoster]

	const findPlayer = (rosterPlayerId) => {
		if (!rosterPlayerId) {
			return null
		}

		return allPlayers.find((player) => player._id === rosterPlayerId) || null
	}

	const getPlayerIdFromRosterPlayerId = (rosterPlayerId) => {
		const player = findPlayer(rosterPlayerId)

		return player?.playerId
	}

	const renderPlayerHistoryLink = (rosterPlayerId, label) => {
		const playerId = getPlayerIdFromRosterPlayerId(rosterPlayerId)

		if (!playerId) {
			return label || ''
		}

		return (
			<Link to={`/${playerId}/history`} className='playerHistoryLink'>
				{label}
			</Link>
		)
	}

	const formatGameDate = (game) => {
		if (!game?.date) {
			return ''
		}

		const monthNames = [
			'',
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		]

		const dateParts = game.date.split(/[-/]/)

		const month = Number(dateParts[0])
		const day = Number(dateParts[1])

		if (!monthNames[month] || !day) {
			return `${game.dayOfWeek || ''} ${game.date}`.trim()
		}

		return `${game.dayOfWeek || ''}, ${monthNames[month]} ${day}`.replace(
			/^,\s*/,
			'',
		)
	}

	const formatGameSummaryDate = (date) => {
		if (!date) {
			return ''
		}

		const [month, day] = date.split(/[-/]/)

		const months = {
			'01': 'Jan.',
			'02': 'Feb.',
			'03': 'Mar.',
			'04': 'Apr.',
			'05': 'May',
			'06': 'June',
			'07': 'July',
			'08': 'Aug.',
			'09': 'Sept.',
			10: 'Oct.',
			11: 'Nov.',
			12: 'Dec.',
		}

		if (!months[month] || !day) {
			return date
		}

		return `${months[month]} ${parseInt(day)}`
	}

	const formatGameTime = (time) => {
		if (!time) {
			return ''
		}

		const [hourValue, minuteValue = '00'] = time.split(':')
		let hour = Number(hourValue)

		if (Number.isNaN(hour)) {
			return time
		}

		const period = hour >= 12 ? 'pm' : 'am'

		hour = hour % 12 || 12

		return `${hour}:${minuteValue} ${period}`
	}

	const formatScore = (score) => {
		if (!score) {
			return ''
		}

		return String(score).replace(/\s*-\s*/, '\u00A0-\u00A0')
	}

	const getGameWinningGoalIndex = () => {
		if (!allGoalsFromThisGame || allGoalsFromThisGame.length === 0) {
			return -1
		}

		if (!winner || winner === 'none') {
			return -1
		}

		if (loadedGame?.status === 'Shootout') {
			return -1
		}

		let losingGoals = 0

		if (winner === loadedGame?.homeTeamName) {
			losingGoals = Number(visitorGoalsTotal || 0)
		} else if (winner === loadedGame?.visitorTeamName) {
			losingGoals = Number(homeGoalsTotal || 0)
		} else {
			return -1
		}

		const winningGoalNumberNeeded = losingGoals + 1

		let winningTeamGoalCount = 0

		for (let i = 0; i < allGoalsFromThisGame.length; i++) {
			const goal = allGoalsFromThisGame[i]

			if (goal.teamName === winner) {
				winningTeamGoalCount++

				if (winningTeamGoalCount === winningGoalNumberNeeded) {
					return i
				}
			}
		}

		return -1
	}

	const gameWinningGoalIndex = getGameWinningGoalIndex()

	const getResultText = () => {
		if (!loadedGame?.score) {
			return 'No final score has been recorded.'
		}

		if (!winner || winner === 'none') {
			return (
				<>
					Tie game. Final score:{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{formatScore(loadedGame.score)}
						</span>
					</span>
				</>
			)
		}

		if (loadedGame.status === 'Overtime') {
			return (
				<>
					<span className='gameSummaryWinner'>{winner}</span> won in{' '}
					<span className='gameSummaryResultHighlight'>OVERTIME</span> by a
					score of{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{formatScore(loadedGame.score)}
						</span>
					</span>
				</>
			)
		}

		if (loadedGame.status === 'Shootout') {
			return (
				<>
					<span className='gameSummaryWinner'>{winner}</span> won in a{' '}
					<span className='gameSummaryResultHighlight'>SHOOTOUT</span> by a
					score of{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{formatScore(loadedGame.score)}
						</span>
					</span>
				</>
			)
		}

		return (
			<>
				<span className='gameSummaryWinner'>{winner}</span> won by a score of{' '}
				<span className='gameSummaryResultHighlight'>
					<span className='gameSummaryScore'>
						{formatScore(loadedGame.score)}
					</span>
				</span>
			</>
		)
	}

	const getMobileFinalText = () => {
		if (loadedGame?.status === 'Overtime') {
			return 'FINAL / OT'
		}

		if (loadedGame?.status === 'Shootout') {
			return 'FINAL / SO'
		}

		return 'FINAL'
	}

	return (
		<div className='gameSummaryPage'>
			{error && (
				<p className='gameSummaryError'>
					{typeof error === 'string'
						? error
						: 'The game summary could not be loaded.'}
				</p>
			)}

			{isLoading && (
				<div className='center gameSummaryLoading'>
					<p>Loading...</p>
				</div>
			)}

			{!isLoading && loadedGame && (
				<main className='Summary'>
					{/* DESKTOP / TABLET HERO */}
					<section className='gameSummaryHeroCard gameSummaryDesktopHero'>
						<div className='gameSummaryMatchup'>
							<div className='gameSummaryVisitor'>
								{loadedGame.visitorTeamName}
							</div>

							<div className='gameSummaryAtSymbol'>@</div>

							<div className='gameSummaryHome'>{loadedGame.homeTeamName}</div>
						</div>

						<div className='gameSummaryAccent'></div>

						<p className='gameSummaryDateTime'>
							{formatGameDate(loadedGame)} at {formatGameTime(loadedGame.time)}
						</p>

						{loadedGame.venueName && (
							<p className='gameSummaryVenue'>📍 {loadedGame.venueName}</p>
						)}

						<div className='gameSummaryDivider'></div>

						<h2 className='gameSummaryResult'>{getResultText()}</h2>

						{loadedGameStats?.summary && (
							<>
								<div className='gameSummaryDivider'></div>

								<p className='gameRecap'>{loadedGameStats.summary}</p>
							</>
						)}
					</section>

					{/* MOBILE HERO */}
					<section className='gameSummaryHeroMobile'>
						<div className='gameSummaryMobileHero'>
							{(loadedGame.divisionName ||
								loadedGame.division ||
								leagueName) && (
								<div className='gameSummaryMobileDivision'>
									{loadedGame.divisionName || loadedGame.division || leagueName}
								</div>
							)}

							<div className='gameSummaryMobileScoreboard'>
								<div className='gameSummaryMobileTeam gameSummaryMobileVisitor'>
									<div className='gameSummaryMobileTeamName'>
										{loadedGame.visitorTeamName}
									</div>

									<div className='gameSummaryMobileScore'>
										{visitorGoalsTotal}
									</div>
								</div>

								<div className='gameSummaryMobileGameInfo'>
									<div className='gameSummaryMobileFinal'>
										{getMobileFinalText()}
									</div>

									<div className='gameSummaryMobileDate'>
										{loadedGame.dayOfWeek},{' '}
										{formatGameSummaryDate(loadedGame.date)}
									</div>

									<div className='gameSummaryMobileTime'>
										{formatGameTime(loadedGame.time)}
									</div>
								</div>

								<div className='gameSummaryMobileTeam gameSummaryMobileHome'>
									<div className='gameSummaryMobileScore'>{homeGoalsTotal}</div>

									<div className='gameSummaryMobileTeamName'>
										{loadedGame.homeTeamName}
									</div>
								</div>
							</div>

							{loadedGame.description && (
								<div className='gameSummaryMobileDescription'>
									{loadedGame.description}
								</div>
							)}

							{winner && winner !== 'none' && (
								<div className='gameSummaryMobileWinner'>
									<span>Winner</span>
									<strong>{winner}</strong>
								</div>
							)}

							{(!winner || winner === 'none') && loadedGame.score && (
								<div className='gameSummaryMobileWinner'>
									<strong>Tied Game</strong>
								</div>
							)}

							{loadedGameStats?.summary && (
								<p className='gameRecap gameSummaryMobileRecap'>
									{loadedGameStats.summary}
								</p>
							)}
						</div>
					</section>

					{/* SCORING SUMMARY */}
					<section className='ScoringSummary'>
						<h2 className='scoringSummaryTitle gameSummaryDesktopSectionTitle'>
							Scoring Summary
						</h2>

						<div className='scoringSummaryAccent gameSummaryDesktopSectionTitle'></div>

						<h3 className='gameSummarySectionTitle'>
							<span>Scoring Summary</span>
						</h3>

						{/* DESKTOP / TABLET GOAL TABLE */}
						<div className='scoringSummaryTableWrapper gameSummaryDesktopTable'>
							<table id='scoringSummary'>
								<thead>
									<tr>
										<th>Pd.</th>
										<th>Time</th>
										<th>Team</th>
										<th>G</th>
										<th>A</th>
										<th>A</th>
									</tr>
								</thead>

								<tbody>
									{allGoalsFromThisGame.map((goal, key) => (
										<tr key={goal._id || key}>
											<td>{goal.period}</td>
											<td>{goal.time}</td>
											<td>{goal.teamName}</td>

											<td>
												{renderPlayerHistoryLink(
													goal.scorer,
													goal.scorerNumberAndName,
												)}

												{key === gameWinningGoalIndex && (
													<span className='gameSummaryDesktopGWG'> (GWG)</span>
												)}
											</td>

											<td>
												{goal.assist1
													? renderPlayerHistoryLink(
															goal.assist1,
															goal.a1NumberAndName,
														)
													: ''}
											</td>

											<td>
												{goal.assist2
													? renderPlayerHistoryLink(
															goal.assist2,
															goal.a2NumberAndName,
														)
													: ''}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* MOBILE GOAL TABLE */}
						<div className='gameSummaryMobileTable'>
							<div className='gameSummaryMobileTableHeader'>
								<div>Goal</div>
								<div>Player</div>
							</div>

							{allGoalsFromThisGame.map((goal, key) => (
								<div className='gameSummaryMobilePlayRow' key={goal._id || key}>
									<div className='gameSummaryMobileInfoCell'>
										{goal.period && (
											<div className='gameSummaryMobilePeriodBadge'>
												{goal.period}
											</div>
										)}

										<div className='gameSummaryMobileMetaTime'>{goal.time}</div>

										<div className='gameSummaryMobileMetaTeam'>
											{goal.teamName}
										</div>
									</div>

									<div className='gameSummaryMobilePlayDetails'>
										<div className='gameSummaryMobileScorer'>
											{renderPlayerHistoryLink(
												goal.scorer,
												goal.scorerNumberAndName,
											)}

											{key === gameWinningGoalIndex && (
												<span className='gameSummaryMobileGWG'>GWG</span>
											)}
										</div>

										{(goal.assist1 || goal.assist2) && (
											<div className='gameSummaryMobileAssists'>
												<span>Asst: </span>

												{goal.assist1 &&
													renderPlayerHistoryLink(
														goal.assist1,
														goal.a1NumberAndName,
													)}

												{goal.assist1 && goal.assist2 && (
													<span className='gameSummaryAssistSeparator'>
														{' '}
														|{' '}
													</span>
												)}

												{goal.assist2 &&
													renderPlayerHistoryLink(
														goal.assist2,
														goal.a2NumberAndName,
													)}
											</div>
										)}

										{!goal.assist1 && !goal.assist2 && (
											<div className='gameSummaryMobileUnassisted'>
												Unassisted
											</div>
										)}
									</div>
								</div>
							))}

							{allGoalsFromThisGame.length === 0 && (
								<div className='gameSummaryMobileEmpty'>No goals recorded.</div>
							)}
						</div>
					</section>

					{/* PENALTIES */}
					<section className='ScoringSummary'>
						<h2 className='scoringSummaryTitle gameSummaryDesktopSectionTitle'>
							Penalties
						</h2>

						<div className='scoringSummaryAccent gameSummaryDesktopSectionTitle'></div>

						<h3 className='gameSummarySectionTitle'>
							<span>Penalties</span>
						</h3>

						{/* DESKTOP / TABLET PENALTY TABLE */}
						<div className='scoringSummaryTableWrapper gameSummaryDesktopTable'>
							<table id='penaltySummary'>
								<thead>
									<tr>
										<th>Pd.</th>
										<th>Time</th>
										<th>Team</th>
										<th>Player</th>
										<th>Type</th>
										<th>Min.</th>
									</tr>
								</thead>

								<tbody>
									{allPenaltiesFromThisGame.map((penalty, key) => {
										const penaltyPlayerName =
											`${penalty.firstName || ''} ${penalty.lastName || ''}`.trim()

										return (
											<tr key={penalty._id || key}>
												<td>{penalty.period}</td>
												<td>{penalty.time}</td>
												<td>{penalty.teamName}</td>

												<td>
													{penalty.rosterPlayerId
														? renderPlayerHistoryLink(
																penalty.rosterPlayerId,
																penaltyPlayerName,
															)
														: penaltyPlayerName}
												</td>

												<td>{penalty.penaltyType}</td>
												<td>{penalty.minutes}</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>

						{/* MOBILE PENALTY TABLE */}
						<div className='gameSummaryMobileTable'>
							<div className='gameSummaryMobileTableHeader'>
								<div></div>
								<div>Player</div>
							</div>

							{allPenaltiesFromThisGame.map((penalty, key) => {
								const penaltyPlayerName =
									`${penalty.firstName || ''} ${penalty.lastName || ''}`.trim()

								return (
									<div
										className='gameSummaryMobilePlayRow'
										key={penalty._id || key}
									>
										<div className='gameSummaryMobileInfoCell'>
											<div className='gameSummaryMobilePeriodBadge'>
												{penalty.period}
											</div>

											<div className='gameSummaryMobileMetaTime'>
												{penalty.time}
											</div>

											<div className='gameSummaryMobileMetaTeam'>
												{penalty.teamName}
											</div>
										</div>

										<div className='gameSummaryMobilePlayDetails'>
											<div className='gameSummaryMobileScorer'>
												{penalty.rosterPlayerId
													? renderPlayerHistoryLink(
															penalty.rosterPlayerId,
															penaltyPlayerName,
														)
													: penaltyPlayerName}
											</div>

											<div className='gameSummaryMobilePenaltyInfo'>
												{penalty.penaltyType}

												{penalty.minutes && (
													<strong> • {penalty.minutes} min.</strong>
												)}
											</div>
										</div>
									</div>
								)
							})}

							{allPenaltiesFromThisGame.length === 0 && (
								<div className='gameSummaryMobileEmpty'>
									No penalties recorded.
								</div>
							)}
						</div>
					</section>
				</main>
			)}

			<Footer />
		</div>
	)
}

export default GameSummary
